/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  Ad,
  AdBreak,
  AdContentTimePosition,
  AdPlaybackPolicy,
  AdPlaybackPolicyHandler,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  Player,
} from '@quickplay/player-interface';
import { Logger } from '@quickplay/foundation';
import {
  getAdBreaksBetween,
  getAdvertBreaksDurationPriorTo,
  getCurrentAdEndTime,
  getTotalAdvertsBreaksDuration,
  isPromosAvailable,
} from '../PromoPlayerUtil';

enum ActivePolicy {
  IDLE = 'idle',
  AUTO_SEEK = 'autoseek',
}

export class PromoPlaybackPolicyHandler implements AdPlaybackPolicyHandler {
  /**
   * The array of all ad breaks.
   * @private
   */
  private adBreaks: AdBreak[] | null = null;
  private readonly fastForwardMode: PlaybackFastForwardMode = PlaybackFastForwardMode.TRICK_FW_PLAY_ALL;
  private readonly rewindMode: PlaybackRewindMode = PlaybackRewindMode.TRICK_RW_PLAY_ALL;
  private readonly autoSeekMode: PlaybackAutoSeekMode = PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL;
  private readonly repeatMode: PlaybackRepeatedMode = PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY;
  private readonly interruptMode: PlaybackInterruptedMode = PlaybackInterruptedMode.INTERRUPT_RESUME;
  private watchedAdBreaks: AdBreak[] = [];

  /**
   * An array that contains a list of seek positions to look for.
   * @private
   */
  private autoSeekPositions: number[] = [];
  /**
   * A value indicating the type of policy being applied.
   * @private
   */
  private policyInPlace: ActivePolicy;

  constructor(
    private readonly player: Player,
    adPlaybackPolicy: AdPlaybackPolicy,
    private readonly logger: Logger,
  ) {
    this.fastForwardMode = adPlaybackPolicy.fastForwardMode;
    this.rewindMode = adPlaybackPolicy.rewindMode;
    this.autoSeekMode = adPlaybackPolicy.autoSeekMode;
    this.repeatMode = adPlaybackPolicy.repeatMode;
    this.interruptMode = adPlaybackPolicy.interruptMode;

    this.logger.log(
      `[PROMO POLICY] [FastForwardMode: ${this.fastForwardMode}] [RewindMode: ${this.rewindMode}] [AutoSeekMode: ${
        this.autoSeekMode
      }] [RepeatMode: ${this.repeatMode}] [InterruptMode: ${this.interruptMode}] [InitialPlaybackTime: ${
        player.playbackOptions && player.playbackOptions.initialPlaybackTime
          ? player.playbackOptions.initialPlaybackTime
          : 0
      }]`,
    );

    this.policyInPlace = ActivePolicy.IDLE;
  }

  setAdBreaks(adBreaks: AdBreak[]): void {
    this.adBreaks = adBreaks;
  }

  playbackInterrupted(): void {
    // no-op
  }

  applySeekPlaybackPolicy = (position: number, adBreaks: AdBreak[]): number => {
    let startPosition: number | undefined = undefined;

    /**
     * Seek Playback policy;
     * - Forward playback policy: If the player'currentTime is less than
     * seekPosition.
     *  - Rewind playback policy: If the player'currentTime is greater than
     * seekPosition.
     */
    if (this.player.currentTime < position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(this.player.currentTime, position, adBreaks);
      startPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    } else if (this.player.currentTime > position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(position, this.player.currentTime, adBreaks);
      startPosition = this.playbackRewindPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    }

    return position;
  };

  private getMidrollAdBreaks(start: number, end: number, adBreaks: AdBreak[]): AdBreak[] {
    const filteredAdBreaks = getAdBreaksBetween(start, end, adBreaks);
    return filteredAdBreaks.filter((adBreak: AdBreak) => adBreak.position === AdContentTimePosition.MIDROLL);
  }

  playbackForwardPolicy(midrollAdBreaks: AdBreak[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.fastForwardMode) {
        case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[0];

          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTime;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[midrollAdBreaks.length - 1];

          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTime;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_SKIP_ALL: {
          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }

    return undefined;
  }

  private handleFastForwardPlayAll(midrollAdBreaks: AdBreak[]): number | undefined {
    // const firstAdBreak = midrollAdBreaks.shift();
    let firstAdBreak: AdBreak | undefined;

    midrollAdBreaks.forEach((adBreak: AdBreak) => {
      if (this.playbackRepeatedRule(adBreak)) {
        if (firstAdBreak === undefined) {
          firstAdBreak = adBreak;
        } else {
          this.autoSeekPositions.push(adBreak.startTime);
        }
      }
    });
    return (firstAdBreak && firstAdBreak.startTime) ?? undefined;
  }
  setWatchedAdBreak(adBreak: AdBreak): void {
    // Check if the adbreak is already marked as watched to avoid adding up duplicate data in an array.
    if (this.repeatMode === PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY && this.isAdBreakPresent(adBreak)) {
      return;
    }

    this.watchedAdBreaks.push(adBreak);
  }

  private isAdBreakPresent(adBreak: AdBreak): boolean {
    return this.watchedAdBreaks.some(watchedAdBreak => watchedAdBreak.startTime === adBreak.startTime);
  }

  playbackRewindPolicy(midrollAdBreaks: AdBreak[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.rewindMode) {
        case PlaybackRewindMode.TRICK_RW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[length - 1];
          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTime;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[0];
          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTime;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_SKIP_ALL: {
          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }
    return undefined;
  }

  playbackRepeatedRule = (adbreak: AdBreak): boolean => {
    if (this.watchedAdBreaks.length === 0) {
      return true;
    }
    switch (this.repeatMode) {
      case PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY: {
        return true;
      }
      case PlaybackRepeatedMode.REPEAT_PLAY_ONCE: {
        if (this.isAdBreakPresent(adbreak)) {
          return false;
        }
      }
    }
    return true;
  };

  resolveSeekIntentForInitialPlayback = (position: number, adBreaks: AdBreak[]): number => {
    const filteredAdBreaks = getAdBreaksBetween(0, position, adBreaks);

    if (!filteredAdBreaks.length) {
      return position;
    }

    const midrollAdBreaks = filteredAdBreaks.filter(
      (adBreak: AdBreak) => adBreak.position === AdContentTimePosition.MIDROLL,
    );

    switch (this.autoSeekMode) {
      case PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL: {
        return position;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForPreroll(filteredAdBreaks);

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_MIDROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForMidroll(
          this.fastForwardMode,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_ALL: {
        const startPosition: number | undefined = this.playbackStartPositionForPlayAll(
          this.fastForwardMode,
          adBreaks,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
    }

    return position;
  };

  resolveSeekIntent = (position: number): number => {
    const nextSeekPosition: number | undefined = this.adBreakEnd();

    if (nextSeekPosition === undefined) {
      return position;
    }

    this.player.seek(nextSeekPosition);

    return nextSeekPosition;
  };

  getAutoSeekPositionIfAny = (): number | null => {
    return null;
  };

  getContentPositionForPlayhead(playhead: number): number {
    return playhead - getAdvertBreaksDurationPriorTo(playhead, this.adBreaks ?? []);
  }

  getPlayheadForContentPosition(position: number): number {
    return position + getAdvertBreaksDurationPriorTo(position, this.adBreaks ?? []);
  }

  getContentDuration(): number {
    return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
  }

  willSeekTo(position: number): number {
    if (this.player.isLive || !this.adBreaks || !this.adBreaks.length) {
      // can never skip live promos
      //this.player.seek(position);

      return position;
    }

    if (this.player.currentTime > 0) {
      return this.applySeekPlaybackPolicy(position, this.adBreaks);
    } else {
      // if player is starting then resolve seek for initial playback position
      return this.resolveSeekIntentForInitialPlayback(position, this.adBreaks);
    }
  }

  getCurrentTime(currentAdBreak: AdBreak | undefined, _ads: Ad[] | undefined): number {
    if (this.player.isLive || !isPromosAvailable(this.adBreaks ?? [])) {
      return this.player.currentTime;
    }

    if (currentAdBreak) {
      const contentTimeBeforeAdBreak = Math.max(0, currentAdBreak.startTime);
      const completedAdsDuration = getAdvertBreaksDurationPriorTo(contentTimeBeforeAdBreak, this.adBreaks ?? []);
      return Math.max(0, contentTimeBeforeAdBreak - completedAdsDuration);
    } else {
      return this.player.currentTime - getAdvertBreaksDurationPriorTo(this.player.currentTime, this.adBreaks ?? []);
    }
  }

  getDuration(_currentAdBreak: AdBreak | undefined, _ads: Ad[] | undefined): number {
    if (this.player.isLive || !isPromosAvailable(this.adBreaks ?? []) || isNaN(this.player.duration)) {
      return this.player.duration;
    }

    return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
  }

  discardAdBreak(currentAdBreak: AdBreak | undefined): void {
    if (!currentAdBreak) {
      return;
    }

    const skipToOffset = currentAdBreak.startTime + currentAdBreak.duration;

    this.player.seek(skipToOffset);
  }

  skipAd(currentAd: Ad | undefined, ads: Ad[] | undefined): void {
    if (!currentAd) {
      return;
    }

    const skipToOffset = getCurrentAdEndTime(currentAd, ads);

    this.player.seek(skipToOffset);
  }

  private playbackStartPositionForPreroll = (adBreaks: AdBreak[]): number | undefined => {
    if (!adBreaks.length) {
      return undefined;
    }

    const firstAdBreak = adBreaks[0];

    if (firstAdBreak.position === AdContentTimePosition.PREROLL) {
      return firstAdBreak.startTime;
    }

    return undefined;
  };

  private playbackStartPositionForMidroll = (
    _fastForwardMode: PlaybackFastForwardMode,
    midrollAdBreaks: AdBreak[],
  ): number | undefined => {
    if (midrollAdBreaks.length) {
      return this.playbackForwardPolicy(midrollAdBreaks);
    }

    return undefined;
  };

  private playbackStartPositionForPlayAll = (
    fastForwardMode: PlaybackFastForwardMode,
    adBreaks: AdBreak[],
    midrollAdBreaks: AdBreak[],
  ): number | undefined => {
    const prerollAdBreak = adBreaks[0];

    if (prerollAdBreak && prerollAdBreak.position === AdContentTimePosition.PREROLL) {
      const autoSeekPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (autoSeekPosition) {
        this.autoSeekPositions.push(autoSeekPosition);
      }
      return prerollAdBreak.startTime;
    } else {
      const startPosition: number | undefined = this.playbackStartPositionForMidroll(fastForwardMode, midrollAdBreaks);

      if (startPosition !== undefined) {
        return startPosition;
      }
    }

    return undefined;
  };

  private adBreakEnd = (): number | undefined => {
    if (!this.autoSeekPositions.length) {
      return undefined;
    }

    const nextPlayhead: number | undefined = this.autoSeekPositions[0];

    if (nextPlayhead) {
      this.updateAutoSeekOnAdBreakEnd();
      return nextPlayhead;
    }

    return undefined;
  };

  private updateAutoSeekOnAdBreakEnd = (): void => {
    this.autoSeekPositions.shift();
    this.updatePolicyStatusOnInterruptionEnd();
  };

  private updatePolicyStatusOnInterruptionEnd = (): void => {
    if (!this.autoSeekPositions.length) {
      this.setIdle();
    } else {
      this.setAutoSeek();
    }

    this.logger.log(`[PROMO POLICY] ad policy in place: ${this.policyInPlace}`);
  };

  private setIdle = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.IDLE;
    }
  };

  private setAutoSeek = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.AUTO_SEEK;
    }
  };
}
